import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';

import { User, regionMap } from '../models';
import { StorageService } from './storage/storage.service';
import { PlatformService } from './platform.service';

@Injectable({
    providedIn: 'root'
})
export class ActiveUserService {
    currentUser$: Observable<User>;

    private currentUserSubject$ = new BehaviorSubject<User>({} as User);

    constructor(
        private storage: StorageService,
        private platformService: PlatformService
    ) {
        this.currentUser$ = this.currentUserSubject$.asObservable();

        this.userProfile().then((user) => {
          if(user){
            user.user_metadata.region = regionMap(user.user_metadata.region);
            this.currentUserSubject$.next(user);
          }
        });
    }

    get currentUserValue(): User {
        return this.currentUserSubject$.value;
    }

    async userProfile(): Promise<User | null> {
        const userData = await this.storage.get('userData');
        try {
            return JSON.parse(userData);
        } catch (_) {
            return null;
        }
    }

    setUser(userProfile: User, accessData: any): Promise<void> {
        this.currentUserSubject$.next(userProfile);
        return this.setSession(accessData, userProfile);
    }

    removeUser(): Promise<void> {
        this.currentUserSubject$.next({} as User);
        return this.unsetSession();
    }

    private async setSession(authResult: User, profile: User): Promise<void> {
        // Web sessions end with the access token. Native keeps a week so the cached
        // content stays usable offline.
        const expiresAt = JSON.stringify(
            this.platformService.isNativeDevice
                ? Date.now() + 7 * 24 * 60 * 60 * 1000
                : Date.now() + authResult.expires_in * 1000
        );

        await Promise.all([
            this.storage.set('expires_at', expiresAt),
            this.storage.set('userData', JSON.stringify(profile))
        ]);
    }

    private async unsetSession(): Promise<void> {
        await Promise.all([
            this.storage.remove('expires_at'),
            this.storage.remove('userData')
        ]);
    }
}
